import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Grid, 
  Card, 
  CardContent, 
  CardMedia, 
  Button, 
  TextField, 
  InputAdornment, 
  IconButton,
  Pagination,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Badge,
  Avatar,
  Divider,
  Stack,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Fade
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  Search as SearchIcon, 
  FilterList as FilterListIcon,
  LocationOn as LocationIcon,
  Store as StoreIcon,
  Visibility as ViewIcon,
  TrendingUp as TrendingIcon,
  Clear as ClearIcon,
  BusinessCenter as BusinessIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  ArrowForward as ArrowForwardIcon,
  LocalPharmacy as LocalPharmacyIcon,
  Storefront as StorefrontIcon,
  Grass as GrassIcon,
  Restaurant as RestaurantIcon,
  Computer as ElectronicsIcon,
  Checkroom as GarmentsIcon,
  Science as ChemicalIcon,
  Build as HardwareIcon,
  FaceRetouchingNatural as CosmeticsIcon,
  LocalFlorist as LocalFloristIcon,
  MenuBook as BookIcon,
  Handyman as HandymanIcon,
  Public as PublicIcon
} from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PageBanner from '../components/PageBanner';
import bazarListBn from '../i18n/bn/bazarList';
import bazarListEn from '../i18n/en/bazarList';
import bazarListNp from '../i18n/np/bazarList';
import bazarListPk from '../i18n/pk/bazarList';
import bazarListSl from '../i18n/sl/bazarList';
import bazarListDe from '../i18n/de/bazarList';
import bazarListAf from '../i18n/af/bazarList';
import bazarListCn from '../i18n/cn/bazarList';
import { useLocation } from '../contexts/LocationContext';

// Styled components
const FilterBar = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(4),
  borderRadius: '6px',
  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
  border: '1px solid rgba(0,0,0,0.05)',
}));

const BazarCard = styled(Card)(({ theme }) => ({
  height: '100%',
  borderRadius: '6px',
  overflow: 'hidden',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  border: '1px solid rgba(0,0,0,0.08)',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 20px 40px rgba(0,0,0,0.12)',
    '& .bazar-image': {
      transform: 'scale(1.05)',
    },
  },
}));

const BazarImage = styled(CardMedia)(({ theme }) => ({
  height: 200,
  transition: 'transform 0.3s ease',
  position: 'relative',
}));

const StatsChip = styled(Chip)(({ theme }) => ({
  backgroundColor: 'rgba(255,255,255,0.95)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255,255,255,0.2)',
  fontWeight: 600,
}));

const PageHeader = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  padding: theme.spacing(6, 0),
  marginBottom: theme.spacing(4),
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'url("/api/placeholder/1920/400") center/cover',
    opacity: 0.2,
  }
}));

// Language will be handled reactively inside the component

// Mock data for bazars with enhanced information
const bazars = [
  { 
    id: 1,
    slug: 'chanchkoir-bazar',
    name: 'চাচকৈড় বাজার', 
    englishName: 'Chanchkoir Bazar',
    image: '/api/placeholder/400/250?text=Chanchkoir+Bazar', 
    storeCount: 145, 
    district: 'নোয়াখালী',
    upazilla: 'চাচকৈড়',
    size: 'বড়',
    specialties: ['তাজা মাছ', 'কৃষি পণ্য', 'হস্তশিল্প'],
    rating: 4.5,
    established: '১৯৮৫',
    dailyVisitors: '৫০০০+',
    description: 'ঐতিহ্যবাহী মাছের বাজার যা স্থানীয় কৃষি পণ্যের জন্য বিখ্যাত।'
  },
  { 
    id: 2,
    slug: 'khubjipur-bazar',
    name: 'খুবজিপুর বাজার', 
    englishName: 'Khubjipur Bazar',
    image: '/api/placeholder/400/250?text=Khubjipur+Bazar', 
    storeCount: 82, 
    district: 'কুমিল্লা',
    upazilla: 'খুবজিপুর',
    size: 'মধ্যম',
    specialties: ['কাপড়', 'গহনা', 'মুদি'],
    rating: 4.2,
    established: '১৯৯২',
    dailyVisitors: '৩০০০+',
    description: 'স্থানীয় কাপড় ও গহনার জন্য প্রসিদ্ধ ঐতিহ্যবাহী বাজার।'
  },
  { 
    id: 3,
    slug: 'nazirpur-bazar',
    name: 'নাজিরপুর বাজার', 
    englishName: 'Nazirpur Bazar',
    image: '/api/placeholder/400/250?text=Nazirpur+Bazar', 
    storeCount: 68, 
    district: 'নোয়াখালী',
    upazilla: 'নাজিরপুর',
    size: 'মধ্যম',
    specialties: ['ধান', 'সবজি', 'পশুর খাদ্য'],
    rating: 4.1,
    established: '১৯৭৮',
    dailyVisitors: '২৫০০+',
    description: 'কৃষি প্রধান এলাকার প্রধান বাজার, ধান ও সবজির জন্য বিখ্যাত।'
  },
  { 
    id: 4,
    slug: 'bildohor-bazar',
    name: 'বিলদোহর বাজার', 
    englishName: 'Bildohor Bazar',
    image: '/api/placeholder/400/250?text=Bildohor+Bazar', 
    storeCount: 123, 
    district: 'ফেনী',
    upazilla: 'বিলদোহর',
    size: 'বড়',
    specialties: ['মসলা', 'শুকনো মাছ', 'চাল'],
    rating: 4.4,
    established: '১৯৮০',
    dailyVisitors: '৪২০০+',
    description: 'মসলা ও শুকনো মাছের পাইকারি বাজার হিসেবে পরিচিত।'
  },
  { 
    id: 5,
    slug: 'chatkhil-bazar',
    name: 'চাটখিল বাজার', 
    englishName: 'Chatkhil Bazar',
    image: '/api/placeholder/400/250?text=Chatkhil+Bazar', 
    storeCount: 95, 
    district: 'নোয়াখালী',
    upazilla: 'চাটখিল',
    size: 'মধ্যম',
    specialties: ['নৌকা', 'জাল', 'মৎস্য সরঞ্জাম'],
    rating: 4.3,
    established: '১৯৮৮',
    dailyVisitors: '৩৫০০+',
    description: 'মৎস্যজীবীদের প্রধান কেন্দ্র, নৌকা ও জালের জন্য বিখ্যাত।'
  },
  { 
    id: 6,
    slug: 'sonaimuri-bazar',
    name: 'সোনাইমুড়ি বাজার', 
    englishName: 'Sonaimuri Bazar',
    image: '/api/placeholder/400/250?text=Sonaimuri+Bazar', 
    storeCount: 87, 
    district: 'নোয়াখালী',
    upazilla: 'সোনাইমুড়ি',
    size: 'মধ্যম',
    specialties: ['ফল', 'সবজি', 'দুগ্ধজাত'],
    rating: 4.0,
    established: '১৯৯৫',
    dailyVisitors: '২৮০০+',
    description: 'তাজা ফল ও সবজির জন্য স্থানীয়ভাবে জনপ্রিয় বাজার।'
  },
  { 
    id: 7,
    slug: 'companiganj-bazar',
    name: 'কোম্পানিগঞ্জ বাজার', 
    englishName: 'Companiganj Bazar',
    image: '/api/placeholder/400/250?text=Companiganj+Bazar', 
    storeCount: 156, 
    district: 'ফেনী',
    upazilla: 'কোম্পানিগঞ্জ',
    size: 'বড়',
    specialties: ['চা পাতা', 'সিগারেট', 'ব্যাংকিং'],
    rating: 4.6,
    established: '১৯৭৫',
    dailyVisitors: '৬০০০+',
    description: 'বাণিজ্যিক কেন্দ্র হিসেবে পরিচিত, চা শিল্পের সাথে সম্পৃক্ত।'
  },
  { 
    id: 8,
    slug: 'senbag-bazar',
    name: 'সেনবাগ বাজার', 
    englishName: 'Senbag Bazar',
    image: '/api/placeholder/400/250?text=Senbag+Bazar', 
    storeCount: 74, 
    district: 'নোয়াখালী',
    upazilla: 'সেনবাগ',
    size: 'ছোট',
    specialties: ['হাঁস-মুরগি', 'ডিম', 'খামার সরঞ্জাম'],
    rating: 3.9,
    established: '১৯৯০',
    dailyVisitors: '২২০০+',
    description: 'পোল্ট্রি ও খামার সংক্রান্ত পণ্যের প্রধান বাজার।'
  },
  { 
    id: 9,
    slug: 'begumganj-bazar',
    name: 'বেগমগঞ্জ বাজার', 
    englishName: 'Begumganj Bazar',
    image: '/api/placeholder/400/250?text=Begumganj+Bazar', 
    storeCount: 118, 
    district: 'নোয়াখালী',
    upazilla: 'বেগমগঞ্জ',
    size: 'বড়',
    specialties: ['বস্ত্র', 'জুতা', 'ইলেকট্রনিক্স'],
    rating: 4.2,
    established: '১৯৮৩',
    dailyVisitors: '৪৮০০+',
    description: 'আধুনিক পণ্যের সাথে ঐতিহ্যের মিশ্রণ, বস্ত্রের জন্য বিখ্যাত।'
  }
];

// Filter options
const districts = [
  { value: '', label: 'সকল জেলা' },
  { value: 'নোয়াখালী', label: 'নোয়াখালী' },
  { value: 'কুমিল্লা', label: 'কুমিল্লা' },
  { value: 'ফেনী', label: 'ফেনী' },
  { value: 'চট্টগ্রাম', label: 'চট্টগ্রাম' },
  { value: 'লক্ষ্মীপুর', label: 'লক্ষ্মীপুর' }
];

const upazillasByDistrict = {
  'নোয়াখালী': [
    { value: '', label: 'সকল উপজেলা' },
    { value: 'চাচকৈড়', label: 'চাচকৈড়' },
    { value: 'নাজিরপুর', label: 'নাজিরপুর' },
    { value: 'চাটখিল', label: 'চাটখিল' },
    { value: 'সোনাইমুড়ি', label: 'সোনাইমুড়ি' },
    { value: 'সেনবাগ', label: 'সেনবাগ' },
    { value: 'বেগমগঞ্জ', label: 'বেগমগঞ্জ' }
  ],
  'কুমিল্লা': [
    { value: '', label: 'সকল উপজেলা' },
    { value: 'খুবজিপুর', label: 'খুবজিপুর' },
    { value: 'দাউদকান্দি', label: 'দাউদকান্দি' },
    { value: 'মেঘনা', label: 'মেঘনা' }
  ],
  'ফেনী': [
    { value: '', label: 'সকল উপজেলা' },
    { value: 'বিলদোহর', label: 'বিলদোহর' },
    { value: 'কোম্পানিগঞ্জ', label: 'কোম্পানিগঞ্জ' },
    { value: 'ফুলগাজী', label: 'ফুলগাজী' }
  ]
};

const bazarSizes = [
  { value: '', label: 'সকল আকার' },
  { value: 'ছোট', label: 'ছোট (৫০টির কম দোকান)' },
  { value: 'মধ্যম', label: 'মধ্যম (৫০-১০০টি দোকান)' },
  { value: 'বড়', label: 'বড় (১০০টির বেশি দোকান)' }
];

const BazarList = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { updateLocation } = useLocation();
  const [currentLanguage, setCurrentLanguage] = useState((typeof window !== 'undefined' && localStorage.getItem('preferredLanguage')) || 'bn');
  const t = currentLanguage === 'en' ? bazarListEn : 
            currentLanguage === 'np' ? bazarListNp : 
            currentLanguage === 'pk' ? bazarListPk : 
            currentLanguage === 'de' ? bazarListDe : 
            currentLanguage === 'sl' ? bazarListSl : 
            currentLanguage === 'af' ? bazarListAf : 
            currentLanguage === 'cn' ? bazarListCn : 
            bazarListBn;
  const bannerData = {
    title: t.banner.title,
    subtitle: t.banner.subtitle,
    breadcrumbs: [
      { label: t.banner.breadcrumbLabel, link: "/food" }
    ],
    currentPlaceholder: t.banner.currentPlaceholder,
    videoSrc: "/background.mp4"
  };
  
  // Location popup state
  const [locationPopupOpen, setLocationPopupOpen] = useState(false);
  const [selectedBazarForLocation, setSelectedBazarForLocation] = useState(null);

  // Bazar menu items from header (same as BazarDetail)
  const bazarMenuItems = [
    { label: 'নিত্যপণ্যের', path: '/daily-bazar', icon: StorefrontIcon },
    { label: 'ঔষধ', path: '/medicine-bazar', icon: LocalPharmacyIcon },
    { label: 'ফ্রেশ খাবার', path: '/food-bazar', icon: RestaurantIcon },
    { label: 'শস্য', path: '/grains', icon: GrassIcon },
    { label: 'জাতীয় পণ্য', path: '/national-bazar', icon: PublicIcon },
    { label: 'গার্মেন্টস', path: '/garments-bazar', icon: GarmentsIcon },
    { label: 'কসমেটিক্স', path: '/cosmetics-bazar', icon: CosmeticsIcon },
    { label: 'বই', path: '/book-bazar', icon: BookIcon },
    { label: 'নার্সারি', path: '/nursery-bazar', icon: LocalFloristIcon },
    { label: 'ইলেক্ট্রনিক্স', path: '/electronics-bazar', icon: ElectronicsIcon },
    { label: 'কেমিক্যাল', path: '/chemical-bazar', icon: ChemicalIcon },
    { label: 'হার্ডওয়্যার', path: '/hardware-bazar', icon: HardwareIcon },
    { label: 'সার্ভিস', path: '/service-bazar', icon: HandymanIcon }
  ];
  
  // Fix scrollbar hiding issue when dropdowns open
  React.useEffect(() => {
    // Override Material-UI's body scroll lock behavior
    const style = document.createElement('style');
    style.innerHTML = `
      .MuiModal-root {
        overflow-y: auto !important;
      }
      body {
        overflow-y: scroll !important;
        padding-right: 0 !important;
      }
      .MuiPopover-root {
        overflow-y: auto !important;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Listen to language change events (from Header) and storage changes
  useEffect(() => {
    const updateLang = () => {
      const lang = (typeof window !== 'undefined' && localStorage.getItem('preferredLanguage')) || 'bn';
      setCurrentLanguage(lang);
    };
    const storageHandler = (e) => {
      if (e.key === 'preferredLanguage') updateLang();
    };
    window.addEventListener('preferredLanguageChanged', updateLang);
    window.addEventListener('storage', storageHandler);
    return () => {
      window.removeEventListener('preferredLanguageChanged', updateLang);
      window.removeEventListener('storage', storageHandler);
    };
  }, []);
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [selectedUpazilla, setSelectedUpazilla] = useState('');
  const [selectedSize, setSelectedSize] = useState('');
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState('name');
  
  // Available upazillas based on selected district
  const [availableUpazillas, setAvailableUpazillas] = useState([{ value: '', label: 'সকল উপজেলা' }]);

  // Update upazillas when district changes
  useEffect(() => {
    if (selectedDistrict && upazillasByDistrict[selectedDistrict]) {
      setAvailableUpazillas(upazillasByDistrict[selectedDistrict]);
    } else {
      setAvailableUpazillas([{ value: '', label: 'সকল উপজেলা' }]);
    }
    setSelectedUpazilla(''); // Reset upazilla when district changes
  }, [selectedDistrict]);

  // Filter and sort bazars
  const filteredBazars = bazars.filter(bazar => {
    const matchesSearch = !searchQuery || 
      bazar.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      bazar.englishName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      bazar.specialties.some(specialty => specialty.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesDistrict = !selectedDistrict || bazar.district === selectedDistrict;
    const matchesUpazilla = !selectedUpazilla || bazar.upazilla === selectedUpazilla;
    const matchesSize = !selectedSize || bazar.size === selectedSize;
    
    return matchesSearch && matchesDistrict && matchesUpazilla && matchesSize;
  }).sort((a, b) => {
    switch (sortBy) {
      case 'storeCount':
        return b.storeCount - a.storeCount;
      case 'rating':
        return b.rating - a.rating;
      case 'visitors':
        return parseInt(b.dailyVisitors.replace(/\D/g, '')) - parseInt(a.dailyVisitors.replace(/\D/g, ''));
      default:
        return a.name.localeCompare(b.name);
    }
  });

  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedDistrict('');
    setSelectedUpazilla('');
    setSelectedSize('');
    setPage(1);
  };

  const hasActiveFilters = searchQuery || selectedDistrict || selectedUpazilla || selectedSize;

  // Pagination
  const itemsPerPage = 9;
  const totalPages = Math.ceil(filteredBazars.length / itemsPerPage);
  const paginatedBazars = filteredBazars.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  const handlePageChange = (event, value) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle setting bazar as current location
  const handleSetBazarLocation = (bazar) => {
    const locationData = {
      district: bazar.district,
      upazilla: bazar.upazilla,
      bazar: bazar.name
    };
    setSelectedBazarForLocation(bazar);
    updateLocation(locationData);
    
    // Show the menu popup
    setLocationPopupOpen(true);
  };

  const handleLocationDialogClose = () => {
    setLocationPopupOpen(false);
    setSelectedBazarForLocation(null);
  };

  const handleMenuItemClick = (path) => {
    setLocationPopupOpen(false);
    setSelectedBazarForLocation(null);
    navigate(path);
  };

  return (
    <>
      <Header />
      
      {/* Enhanced Banner Section with Filter Overlay */}
      <Box sx={{ position: 'relative', minHeight: '500px' }}>
        <PageBanner 
          title= {bannerData.title}
          subtitle= {bannerData.subtitle}
          breadcrumbs={bannerData.breadcrumbs}
          placeholders={bannerData.placeholders}
          currentPlaceholder={bannerData.currentPlaceholder}
          sx={{
            minHeight: '500px',
            '& .banner-content': {
              minHeight: '500px'
            }
          }}
        />
        
        {/* Filter Section Positioned 30% inside Banner */}
        <Container maxWidth="xl" sx={{ 
          position: 'absolute', 
          bottom: '-8%', 
          left: '50%', 
          transform: 'translateX(-50%)',
          width: '100%',
          zIndex: 10
        }}>
          <FilterBar elevation={3} sx={{
            backgroundColor: 'white',
            borderRadius: '6px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            border: '1px solid rgba(255,255,255,0.2)'
          }}>
          <Typography variant="h6" gutterBottom sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            mb: 3,
            fontWeight: 600,
            color: 'text.primary'
          }}>
            <FilterListIcon sx={{ mr: 1, color: 'primary.main' }} />
            {t.filter.title}
          </Typography>
          
          <Grid container spacing={3} alignItems="center">
            {/* Search Bar */}
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                placeholder={t.filter.searchPlaceholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                variant="outlined"
                size="medium"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '6px',
                    backgroundColor: 'grey.50',
                    '&:hover': {
                      backgroundColor: 'grey.100',
                    },
                    '&.Mui-focused': {
                      backgroundColor: 'white',
                    }
                  }
                }}
              />
            </Grid>

            {/* District Dropdown */}
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="medium">
                <InputLabel>{t.filter.district}</InputLabel>
                <Select
                  value={selectedDistrict}
                  label={t.filter.district}
                  onChange={(e) => setSelectedDistrict(e.target.value)}
                  sx={{
                    borderRadius: '6px',
                    backgroundColor: 'grey.50',
                    '&:hover': {
                      backgroundColor: 'grey.100',
                    },
                    '&.Mui-focused': {
                      backgroundColor: 'white',
                    }
                  }}
                >
                  {districts.map((district) => (
                    <MenuItem key={district.value} value={district.value}>
                      {district.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Upazilla Dropdown */}
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="medium">
                <InputLabel>{t.filter.upazilla}</InputLabel>
                <Select
                  value={selectedUpazilla}
                  label={t.filter.upazilla}
                  onChange={(e) => setSelectedUpazilla(e.target.value)}
                  disabled={!selectedDistrict}
                  sx={{
                    borderRadius: '6px',
                    backgroundColor: selectedDistrict ? 'grey.50' : 'grey.200',
                    '&:hover': {
                      backgroundColor: selectedDistrict ? 'grey.100' : 'grey.200',
                    },
                    '&.Mui-focused': {
                      backgroundColor: 'white',
                    }
                  }}
                >
                  {availableUpazillas.map((upazilla) => (
                    <MenuItem key={upazilla.value} value={upazilla.value}>
                      {upazilla.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Bazar Size */}
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="medium">
                <InputLabel>{t.filter.size}</InputLabel>
                <Select
                  value={selectedSize}
                  label={t.filter.size}
                  onChange={(e) => setSelectedSize(e.target.value)}
                  sx={{
                    borderRadius: '6px',
                    backgroundColor: 'grey.50',
                    '&:hover': {
                      backgroundColor: 'grey.100',
                    },
                    '&.Mui-focused': {
                      backgroundColor: 'white',
                    }
                  }}
                >
                  {bazarSizes.map((size) => (
                    <MenuItem key={size.value} value={size.value}>
                      {size.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Sort By */}
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="medium">
                <InputLabel>{t.filter.sortBy}</InputLabel>
                <Select
                  value={sortBy}
                  label={t.filter.sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  sx={{
                    borderRadius: '6px',
                    backgroundColor: 'grey.50',
                    '&:hover': {
                      backgroundColor: 'grey.100',
                    },
                    '&.Mui-focused': {
                      backgroundColor: 'white',
                    }
                  }}
                >
                  <MenuItem value="name">{t.filter.sortOptions.name}</MenuItem>
                  <MenuItem value="storeCount">{t.filter.sortOptions.storeCount}</MenuItem>
                  <MenuItem value="rating">{t.filter.sortOptions.rating}</MenuItem>
                  <MenuItem value="visitors">{t.filter.sortOptions.visitors}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

                         {/* Clear Filters */}
             <Grid item xs={12} md={1}>
               <Button
                 variant="outlined"
                 color="error"
                 onClick={handleClearFilters}
                 disabled={!hasActiveFilters}
                 startIcon={<ClearIcon />}
                 fullWidth
                 sx={{
                   borderRadius: '6px',
                   py: 1.8,
                   fontWeight: 600,
                   textTransform: 'none',
                   whiteSpace: 'nowrap',
                   minWidth: 'auto'
                 }}
                 >
                  {isMobile ? t.filter.clearShort : t.filter.clearAll}
               </Button>
             </Grid>
          </Grid>

          {/* Active Filters Display */}
          {hasActiveFilters && (
            <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t.filter.activeFilters}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {searchQuery && (
                  <Chip
                    label={`${t.filter.labels.search}: "${searchQuery}"`}
                    onDelete={() => setSearchQuery('')}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                )}
                {selectedDistrict && (
                  <Chip
                    label={`${t.filter.labels.district}: ${selectedDistrict}`}
                    onDelete={() => setSelectedDistrict('')}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                )}
                {selectedUpazilla && (
                  <Chip
                    label={`${t.filter.labels.upazilla}: ${selectedUpazilla}`}
                    onDelete={() => setSelectedUpazilla('')}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                )}
                {selectedSize && (
                  <Chip
                    label={`${t.filter.labels.size}: ${selectedSize}`}
                    onDelete={() => setSelectedSize('')}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                )}
              </Stack>
            </Box>
          )}
          </FilterBar>
        </Container>
      </Box>

      {/* Main Content Area */}
      <Container maxWidth="xl" sx={{ mt: 12, mb: 8 }}>
        {/* Results Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {filteredBazars.length > 0 ? (
              <>
                <BusinessIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'primary.main' }} />
                {currentLanguage === 'en' 
                  ? `${filteredBazars.length} ${t.results.foundSuffix}`
                  : `${filteredBazars.length} ${t.results.foundSuffix}`}
              </>
            ) : (
              t.results.none
            )}
          </Typography>
          
          {filteredBazars.length > 0 && (
            <Typography variant="body2" color="text.secondary">
              {t.results.page} {page} / {totalPages}
            </Typography>
          )}
        </Box>

        {/* Bazar Grid */}
        {filteredBazars.length === 0 ? (
          <Paper sx={{ p: 6, textAlign: 'center', borderRadius: '6px' }}>
            <BusinessIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" gutterBottom color="text.secondary">
              {t.empty.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              {t.empty.subtitle}
            </Typography>
            <Button 
              variant="contained" 
              onClick={handleClearFilters}
              sx={{ borderRadius: '6px', px: 4 }}
            >
              {t.empty.clear}
            </Button>
          </Paper>
        ) : (
          <>
            <Grid container spacing={3}>
              {paginatedBazars.map((bazar) => (
                <Grid item xs={12} sm={6} lg={4} key={bazar.id}>
                  <BazarCard>
                                         <Box sx={{ position: 'relative' }}>
                       <BazarImage
                         component="img"
                         image={bazar.image}
                         alt={bazar.name}
                         className="bazar-image"
                         onError={(e) => {
                           e.target.src = '/images/bazar.png';
                         }}
                       />
                      <StatsChip
                        label={bazar.size}
                        size="small"
                        sx={{ position: 'absolute', top: 12, left: 12 }}
                      />
                      <StatsChip
                        label={`★ ${bazar.rating}`}
                        size="small"
                        sx={{ position: 'absolute', top: 12, right: 12 }}
                      />
                    </Box>
                    
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="h6" component="h2" gutterBottom sx={{ 
                        fontWeight: 600, 
                        color: 'text.primary',
                        lineHeight: 1.2 
                      }}>
                        {bazar.name}
                      </Typography>
                      
                      <Typography variant="body2" color="text.secondary" gutterBottom sx={{ 
                        fontStyle: 'italic',
                        fontSize: '0.875rem'
                      }}>
                        {bazar.englishName}
                      </Typography>

                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
                        <Chip
                          icon={<LocationIcon />}
                          label={`${bazar.upazilla}, ${bazar.district}`}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.75rem' }}
                        />
                      </Box>

                      <Typography variant="body2" color="text.secondary" paragraph sx={{ 
                        fontSize: '0.875rem',
                        lineHeight: 1.5,
                        height: '3em',
                        overflow: 'hidden',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical'
                      }}>
                        {bazar.description}
                      </Typography>

                      <Grid container spacing={1} sx={{ mb: 2 }}>
                        <Grid item xs={6}>
                          <Box sx={{ textAlign: 'center', p: 1, backgroundColor: 'grey.50', borderRadius: '6px' }}>
                            <Typography variant="h6" color="primary" sx={{ fontWeight: 700 }}>
                              {bazar.storeCount}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {t.card.stores}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6}>
                          <Box sx={{ textAlign: 'center', p: 1, backgroundColor: 'grey.50', borderRadius: '6px' }}>
                            <Typography variant="h6" color="success.main" sx={{ fontWeight: 700 }}>
                              {bazar.dailyVisitors}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {t.card.dailyVisitors}
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>

                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                          {t.card.specialties}
                        </Typography>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {bazar.specialties.slice(0, 3).map((specialty, index) => (
                            <Chip
                              key={index}
                              label={specialty}
                              size="small"
                              variant="outlined"
                              sx={{ 
                                fontSize: '0.7rem', 
                                height: '24px',
                                backgroundColor: 'primary.50',
                                borderColor: 'primary.200'
                              }}
                            />
                          ))}
                        </Stack>
                      </Box>

                      <Divider sx={{ my: 2 }} />

                      <Box sx={{ display: 'flex', gap: 1 }}>
                                                                         <Button
                          size="small"
                          variant="outlined"
                          component={Link}
                          to={`/bazars/${bazar.slug}`}
                          startIcon={<ViewIcon />}
                          sx={{ 
                            flex: 1, 
                            borderRadius: '6px', 
                            textTransform: 'none',
                            fontWeight: 600
                          }}
                        >
                          {t.card.details}
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => handleSetBazarLocation(bazar)}
                          startIcon={<StoreIcon />}
                          sx={{ 
                            flex: 1, 
                            borderRadius: '6px', 
                            textTransform: 'none',
                            fontWeight: 600,
                            boxShadow: 'none',
                            '&:hover': {
                              boxShadow: 2
                            }
                          }}
                        >
                          {t.card.visit}
                        </Button>
                      </Box>
                    </CardContent>
                  </BazarCard>
                </Grid>
              ))}
            </Grid>

            {/* Pagination */}
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                  size="large"
                  showFirstButton
                  showLastButton
                  sx={{
                    '& .MuiPaginationItem-root': {
                      borderRadius: '6px',
                      fontWeight: 600
                    }
                  }}
                />
              </Box>
            )}
          </>
        )}
      </Container>
      <Footer />
      
      {/* Beautiful Location Change Popup */}
      <Dialog
        open={locationPopupOpen}
        onClose={handleLocationDialogClose}
        TransitionComponent={Fade}
        TransitionProps={{ timeout: 600 }}
        maxWidth="md"
        fullWidth
        disableScrollLock={true}
        PaperProps={{
          sx: {
            borderRadius: '6px',
            background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
            boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
            overflow: 'visible'
          }
        }}
      >
        <DialogTitle sx={{ 
          textAlign: 'center', 
          pb: 1,
          background: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
          color: 'white',
          borderRadius: '6px 6px 0 0',
          position: 'relative'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
            <CheckCircleIcon sx={{ fontSize: 40, mr: 2 }} />
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {t.dialog.title}
            </Typography>
          </Box>
          <Typography variant="body1" sx={{ opacity: 0.9 }}>
            {t.dialog.selected}
          </Typography>
          <IconButton
            onClick={handleLocationDialogClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.1)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 4, pt: 3 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <LocationIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
              {selectedBazarForLocation?.name}
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              {t.dialog.instruction}
            </Typography>
          </Box>
          
          <Typography variant="h6" sx={{ mb: 3, textAlign: 'center', color: 'primary.main', fontWeight: 600 }}>
            {t.dialog.categoryPrompt}
          </Typography>
          
          <Grid container spacing={2}>
            {bazarMenuItems.map((item, index) => {
              const IconComponent = item.icon;
              return (
                <Grid item xs={6} sm={4} md={3} key={index}>
                  <Paper
                    elevation={0}
                    onClick={() => handleMenuItemClick(item.path)}
                    sx={{
                      p: 2,
                      textAlign: 'center',
                      cursor: 'pointer',
                      border: '2px solid',
                      borderColor: 'grey.200',
                      borderRadius: '6px',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        borderColor: 'primary.main',
                        backgroundColor: 'primary.50',
                        transform: 'translateY(-4px)',
                        boxShadow: '0 8px 25px rgba(0,0,0,0.1)'
                      }
                    }}
                  >
                    <IconComponent sx={{ 
                      fontSize: 40, 
                      color: 'primary.main', 
                      mb: 1,
                      transition: 'color 0.3s ease'
                    }} />
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: 600,
                        color: 'text.primary',
                        fontSize: '0.875rem'
                      }}
                    >
                      {item.label}
                    </Typography>
                    <ArrowForwardIcon sx={{ 
                      fontSize: 16, 
                      color: 'text.secondary',
                      mt: 0.5,
                      opacity: 0.7
                    }} />
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </DialogContent>
        
        <DialogActions sx={{ 
          p: 3, 
          pt: 1,
          justifyContent: 'space-between',
          borderTop: '1px solid',
          borderColor: 'divider'
        }}>
          <Typography variant="body2" color="text.secondary">
            {currentLanguage === 'en' 
              ? `${t.dialog.bottomNotePrefix} ${selectedBazarForLocation?.name}`
              : `${t.dialog.bottomNotePrefix} ${selectedBazarForLocation?.name} ${t.dialog.bottomNoteSuffix}`}
          </Typography>
          <Button
            onClick={handleLocationDialogClose}
            variant="outlined"
            sx={{
              borderRadius: '6px',
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            {t.dialog.close}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BazarList;